import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import {
  PENDING_TENANT_RNC_STORAGE_KEY,
  TENANT_PREFERENCE_STORAGE_KEY,
} from "../lib/authoritativeTenant";
import { Building2, Lock, Mail, ArrowRight, Loader2 } from "lucide-react";

export const Login: React.FC = () => {
  const [step, setStep] = useState<"rnc" | "credentials">("rnc");
  const [rnc, setRnc] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);

  const handleVerifyRnc = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc(
        "verify_tenant_rnc",
        {
          target_rnc: rnc.toUpperCase(),
        },
      );
      if (rpcError) throw rpcError;

      if (data && data.valid === true) {
        setTenantName(data.name);
        setStep("credentials");
      } else {
        throw new Error(
          "El RNC ingresado no está registrado. Verifique e intente nuevamente.",
        );
      }
    } catch (err: any) {
      setError(err.message || "Error de conexión. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    let authenticated = false;

    try {
      sessionStorage.setItem(
        PENDING_TENANT_RNC_STORAGE_KEY,
        rnc.toUpperCase(),
      );
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          throw new Error(
            "Credenciales incorrectas. Verifique correo o contraseña.",
          );
        }
        if (error.message.includes("Email not confirmed")) {
          throw new Error(
            "Correo no verificado. Revise su bandeja de entrada.",
          );
        }
        throw new Error("Error de autenticación. Intente nuevamente.");
      }
      authenticated = Boolean(data.session?.user);

      window.location.assign("/");
    } catch (err: any) {
      localStorage.removeItem(TENANT_PREFERENCE_STORAGE_KEY);
      sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
      if (authenticated) {
        await supabase.auth.signOut();
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth handler
  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      sessionStorage.setItem(
        PENDING_TENANT_RNC_STORAGE_KEY,
        rnc.toUpperCase(),
      );
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      sessionStorage.removeItem(PENDING_TENANT_RNC_STORAGE_KEY);
      setError(err.message || "Error con Google Login");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50 flex items-center justify-center p-4 selection:bg-black selection:text-white">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-3xl border border-brand-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-gray-900/20">
            <Building2 className="w-8 h-8 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-2">
            OsirisBrain ERP
          </h2>
          <p className="text-sm text-gray-500 font-medium tracking-wide uppercase">
            {step === "rnc"
              ? "Autenticación Comercial"
              : tenantName || "Sovereign Hub"}
          </p>
        </div>

        {error && (
          <div className="p-4 bg-red-50/50 border border-red-100 rounded-2xl text-sm text-red-600 font-medium text-center animate-in slide-in-from-top-2">
            {error}
          </div>
        )}

        {step === "rnc" ? (
          <form
            className="mt-10 space-y-6 animate-in slide-in-from-right-4"
            onSubmit={handleVerifyRnc}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  RNC o Cédula (Inquilino)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    required
                    value={rnc}
                    onChange={(e) =>
                      setRnc(e.target.value.replace(/[^0-9A-Z-]/g, ""))
                    }
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="Ej. 130456789"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2 ml-1">
                  Ingrese el RNC de su empresa
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                disabled={loading || rnc.length < 5}
                className="group relative flex w-full justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-2xl text-white bg-gray-900 hover:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-all active:scale-[0.98] disabled:opacity-70"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Validar Empresa{" "}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </button>
            </div>
          </form>
        ) : (
          <form
            className="mt-10 space-y-6 animate-in slide-in-from-right-4"
            onSubmit={handleAuth}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Correo Electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="admin@empresa.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 ml-1">
                  Contraseña Soberana
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-11 pr-4 py-3.5 bg-brand-50 border-0 rounded-2xl text-gray-900 font-medium placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-500 transition-all sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="group relative flex w-full justify-center py-4 px-4 border border-transparent text-sm font-bold rounded-2xl text-white bg-gray-900 hover:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-all active:scale-[0.98] disabled:opacity-70"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Acceder a Bóveda{" "}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </button>

              <div className="relative flex items-center justify-center my-2">
                <div className="border-t border-gray-200 flex-1"></div>
                <span className="px-3 text-xs text-gray-400 font-medium">
                  o continúe con
                </span>
                <div className="border-t border-gray-200 flex-1"></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="flex w-full justify-center items-center gap-3 py-3.5 px-4 border-2 border-gray-200 text-sm font-bold rounded-2xl text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition-all active:scale-[0.98] disabled:opacity-70"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continuar con Google
              </button>

              <button
                type="button"
                onClick={() => {
                  setStep("rnc");
                  setError(null);
                }}
                disabled={loading}
                className="relative flex w-full justify-center py-4 px-4 border-2 border-brand-100 text-sm font-bold rounded-2xl text-gray-700 bg-white hover:bg-brand-100 hover:border-brand-200 focus:outline-none transition-all active:scale-[0.98] disabled:opacity-70"
              >
                Cambiar de Empresa (RNC)
              </button>

            </div>
          </form>
        )}

        <div className="text-center pt-4 space-y-3">
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors"
          >
            ¿No tienes empresa? Regístrate aquí
          </Link>
          <p className="text-xs text-gray-400">
            Protegido por Supabase Row Level Security (RLS)
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
